import { PROJECTS } from "./projects";
import technologySlugs from "./technology-slugs.json";

/**
 * Keep TECHNOLOGY_SLUGS in sync with technology-slugs.json (used by gatsby-node).
 */
export const TECHNOLOGIES = [
  {
    slug: "react",
    title: "React based development",
    navLabel: "React",
    icon: "React",
    seoTitle: "React developer for SaaS",
    seoDescription:
      "Senior React engineer for established SaaS products - design systems, performance, and frontend architecture.",
    description:
      "I use React on established SaaS products - design systems, complex UI, and client apps that need to stay maintainable as the product grows.",
    features: [
      "Reusable components",
      "Strong performance habits",
      "Scalable app structure",
      "Works well with TypeScript",
      "Design system friendly",
    ],
    projectSlugs: [
      "condexo-pay",
      "condexo-property-management",
      "shakespeers",
      "harmindra-international",
      "jiffy-design-lab",
    ],
    advantages: {
      title: "My approach to React work",
      items: [
        "Architect React frontends for established SaaS products where consistency and long-term maintainability matter",
        "Build design-system foundations and reusable component patterns that keep UI coherent as the product expands",
        "Own complex client flows - payments, property workflows, content platforms - with clear structure and performance budgets",
        "Bridge design and engineering so shipped interfaces stay faithful to intent without slowing delivery",
      ],
    },
  },
  {
    slug: "vue",
    title: "Vue based development",
    navLabel: "Vue",
    icon: "Vue",
    seoTitle: "Vue developer for SaaS",
    seoDescription:
      "Senior Vue engineer for enterprise SaaS - component libraries, web components, and long-lived product frontends.",
    description:
      "I use Vue on established product frontends, including design systems and web components, where clear structure and steady delivery matter.",
    features: [
      "Component-driven UI",
      "Design system work",
      "Web components fit",
      "Clear data flow",
      "Easy for teams to extend",
    ],
    projectSlugs: ["meltwater", "jiffy"],
    advantages: {
      title: "How I approach Vue work",
      items: [
        "Lead Vue frontends inside large, long-lived SaaS products where shared UI has to scale across teams",
        "Build and evolve design-system pieces and web components that other squads can adopt with confidence",
        "Keep architecture and data flow clear so new screens fit the existing system instead of creating one-off paths",
        "Collaborate with design and product to ship production UI that stays consistent under continuous iteration",
      ],
    },
    note: {
      title: "Vue work in production",
      body: "At Meltwater I contribute as a senior frontend developer on a publishing product built with Vue and Stencil web components - shared UI, calendars, and surfaces that must fit an established system. Previously at Jiffy I owned work in a large Vue frontend integrated with Rails. In both environments the mandate is the same: harden the UI layer, protect consistency, and keep the product moving without accumulating structural debt.",
    },
  },
];

export const TECHNOLOGY_SLUGS = TECHNOLOGIES.map((tech) => tech.slug);

const slugsMatchRegistry =
  TECHNOLOGY_SLUGS.length === technologySlugs.length &&
  TECHNOLOGY_SLUGS.every((slug, index) => slug === technologySlugs[index]);

if (!slugsMatchRegistry) {
  throw new Error(
    "technology-slugs.json is out of sync with TECHNOLOGIES. Update both so gatsby-node createPages stays correct.",
  );
}

export const TECHNOLOGY_BADGE_ALIASES = {
  React: "react",
  Vue: "vue",
  "Gatsby (React)": "react",
};

export const getTechnologyBySlug = (slug) =>
  TECHNOLOGIES.find((tech) => tech.slug === slug) || null;

export const getTechnologyPath = (slug) => `/technology/${slug}/`;

export const getTechnologyHrefForBadge = (badgeLabel) => {
  const slug = TECHNOLOGY_BADGE_ALIASES[badgeLabel];
  return slug ? getTechnologyPath(slug) : null;
};

export const getProjectsForTechnology = (technology) => {
  if (!technology?.projectSlugs?.length) {
    return [];
  }

  const bySlug = new Map(PROJECTS.map((project) => [project.slug, project]));
  return technology.projectSlugs
    .map((slug) => bySlug.get(slug))
    .filter(Boolean);
};
